import type { ParsedQuestion } from '../../components/survey/types';

/** One decision-process slot (first client-present shelves in shopped order). */
export interface PipedSlot {
  slot: number;
  shelf_id: string;
  chosen_product_id: string;
  /** The shelf's packs with the chosen one outlined. */
  shelf_image_url: string;
}

/** One synthetic pair screen, in presented order. */
export interface SyntheticSlot {
  slot: number;
  left_url: string;
  right_url: string;
  /** Design codes shown as Pack A (left) and Pack B (right). */
  left?: string;
  right?: string;
}

/** One valuation product, in the respondent's shared order. */
export interface ValuationSlot {
  slot: number;
  product_id: string;
  image_url: string;
}

/** GET /api/eyetracking/piped-media/{rid}. */
export interface PipedPayload {
  module_order: string;
  decision: PipedSlot[];
  synthetic: SyntheticSlot[];
  valuation: ValuationSlot[];
}

export type PipedMediaState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; data: PipedPayload }
  | { status: 'error' };

/** What the respondent was shown on a piped question, stored with their answer. */
export type ShownStimulus = Record<string, unknown>;

export interface PipedResolution<Q> {
  question: Q;
  /** Set when the question was filled with this respondent's own content. */
  stimulus?: ShownStimulus;
  /** Waiting for the payload: hold the question off screen. */
  pending: boolean;
  /** Nothing to show (other module order, slot not reached, or the lookup failed). */
  skip: boolean;
}

export const surveyUsesPipedMedia = (questions: ParsedQuestion[] | undefined): boolean =>
  !!questions?.some((q) => q.piped_media);

const shown = <Q>(question: Q, stimulus?: ShownStimulus): PipedResolution<Q> => ({
  question,
  pending: false,
  skip: false,
  ...(stimulus ? { stimulus } : {}),
});
/** The slot's content without its position number (the question id already carries that). */
const withoutSlot = (slotContent: { slot: number }): ShownStimulus => {
  const rest: ShownStimulus = { ...slotContent };
  delete rest.slot;
  return rest;
};
const skipped = <Q>(question: Q): PipedResolution<Q> => ({ question, pending: false, skip: true });

/** Fill a piped question with the respondent's own content, or say why it can't be shown. */
export function resolvePipedQuestion<Q extends ParsedQuestion>(
  question: Q | null,
  state: PipedMediaState,
): PipedResolution<Q | null> {
  const piped = question?.piped_media;
  if (!question || !piped) return shown(question);
  if (state.status === 'idle' || state.status === 'loading') {
    return { question, pending: true, skip: false };
  }
  if (state.status === 'error') return skipped(question);

  const { data } = state;
  if (piped.module_order && piped.module_order !== data.module_order) return skipped(question);

  switch (piped.source) {
    case 'realeye': {
      const slot = data.decision.find((s) => s.slot === piped.slot);
      if (!slot) return skipped(question);
      return shown({ ...question, media_urls: [slot.shelf_image_url] }, withoutSlot(slot));
    }
    case 'synthetic': {
      const slot = data.synthetic.find((s) => s.slot === piped.slot);
      if (!slot) return skipped(question);
      return shown(
        { ...question, option_media: { 0: [slot.left_url], 1: [slot.right_url] } },
        withoutSlot(slot),
      );
    }
    case 'valuation': {
      const slot = data.valuation.find((s) => s.slot === piped.slot);
      if (!slot) return skipped(question);
      return shown({ ...question, media_urls: [slot.image_url] }, withoutSlot(slot));
    }
    default:
      return shown(question);
  }
}
